// Lists (gap #2, saved views): the starter lists every office has, and how a list's spec is changed
// from the screen (which statuses, a "contains" search, the sort, the columns) and its cells said.
// Pure, so it's tested without a browser; the server (views.run) decides what each person may see.

import { dateText, dateTimeText } from '@flowology/kernel';
import { jobStatusWord } from '@flowology/ui-web/words';
import { money } from '../books/model.ts';
import { zonedInstant } from '../book-model.ts';

export type Cond = { field: string; op: string; value?: unknown };
export type Filter = Cond | { all: Filter[] } | { any: Filter[] };

export interface ViewSpec {
  entity: string;
  filter?: Filter;
  columns?: string[];
  sort?: { field: string; dir: 'asc' | 'desc' }[];
  limit?: number;
}

export interface FieldInfo {
  field: string;
  label: string;
  type: string;
  values: string[] | null;
}
export interface EntityInfo {
  entity: string;
  label: string;
  fields: FieldInfo[];
}

export interface SavedView {
  id: string;
  entity: string;
  name: string;
  spec: Record<string, unknown>;
  filtersHidden: boolean;
  shared: boolean;
  mine: boolean;
  canEdit: boolean;
}

export interface ViewResult {
  entity: string;
  columns: { field: string; label: string; type: string }[];
  rows: Record<string, unknown>[];
  /** More rows than this answer: read on from the rows already read (`offset`). */
  truncated: boolean;
  /** Every row the list's filter takes, counted by the server. */
  total?: number;
}

/** How many rows a list reads at a time; the rest come on "Load more" (list caps L1). */
export const VIEW_PAGE = 100;

/** views.run's input for a list as it is now, from the rows already read. */
export const viewInput = (spec: ViewSpec, offset = 0): Record<string, unknown> => ({
  spec: { ...spec, limit: spec.limit ?? VIEW_PAGE },
  ...(offset ? { offset } : {}),
});

/** A list's next page under the rows read: each row once (one that moved between reads isn't drawn twice). */
export function addViewPage(had: ViewResult, page: ViewResult): ViewResult {
  const seen = new Set(had.rows.map((r) => String(r['id'])));
  return {
    ...page,
    rows: [...had.rows, ...page.rows.filter((r) => !seen.has(String(r['id'])))],
  };
}

/** Monday 00:00 to the next Monday 00:00 of the week `today` is in, as instants in the company's zone. */
export function weekRange(today: string, timeZone: string): { start: string; end: string } {
  const m = new Date(`${today}T12:00:00Z`);
  m.setUTCDate(m.getUTCDate() - ((m.getUTCDay() + 6) % 7)); // back to Monday (getUTCDay: 0 is Sunday)
  const start = m.toISOString().slice(0, 10);
  m.setUTCDate(m.getUTCDate() + 7);
  const next = m.toISOString().slice(0, 10);
  return { start: zonedInstant(start, 0, timeZone), end: zonedInstant(next, 0, timeZone) };
}

/** The four lists every office starts with (the lead's ruling: everyone with the list's permission). */
export function starters(today: string, timeZone: string): { key: string; name: string; spec: ViewSpec }[] {
  const week = weekRange(today, timeZone);
  return [
    {
      key: 'open-jobs',
      name: 'Open jobs',
      spec: {
        entity: 'jobs',
        filter: { field: 'status', op: 'in', value: ['open', 'in_progress', 'awaiting_review'] },
        columns: ['number', 'customer', 'summary', 'status', 'nextVisit'],
        sort: [{ field: 'nextVisit', dir: 'asc' }],
      },
    },
    {
      key: 'this-week',
      name: 'Jobs this week',
      spec: {
        entity: 'jobs',
        filter: {
          all: [
            { field: 'status', op: 'ne', value: 'cancelled' },
            { field: 'nextVisit', op: 'gte', value: week.start },
            { field: 'nextVisit', op: 'lt', value: week.end },
          ],
        },
        columns: ['nextVisit', 'number', 'customer', 'summary', 'status'],
        sort: [{ field: 'nextVisit', dir: 'asc' }],
      },
    },
    {
      key: 'unpaid',
      name: 'Unpaid invoices',
      spec: {
        entity: 'invoices',
        filter: {
          all: [
            { field: 'balance', op: 'gt', value: 0 },
            { field: 'status', op: 'in', value: ['finalized', 'sent', 'partially_paid'] },
          ],
        },
        columns: ['number', 'customer', 'job', 'dueOn', 'balance'],
        sort: [{ field: 'dueOn', dir: 'asc' }],
      },
    },
    {
      key: 'follow-ups',
      name: 'Follow-ups',
      spec: {
        entity: 'estimates',
        filter: { field: 'status', op: 'in', value: ['presented', 'sent'] },
        columns: ['number', 'customer', 'title', 'status', 'createdAt'],
        sort: [{ field: 'createdAt', dir: 'asc' }],
      },
    },
  ];
}

const isCond = (f: Filter): f is Cond => 'field' in f;
const parts = (f: Filter | undefined): Filter[] => (!f ? [] : isCond(f) || 'any' in f ? [f] : f.all);
const join = (fs: Filter[]): Filter | undefined =>
  fs.length === 0 ? undefined : fs.length === 1 ? fs[0] : { all: fs };

/** The values a list picks for an enum field ("in" or "eq" at its top level); null when it picks all. */
export function picked(spec: ViewSpec, field: string): string[] | null {
  for (const f of parts(spec.filter))
    if (isCond(f) && f.field === field && (f.op === 'in' || f.op === 'eq'))
      return Array.isArray(f.value) ? f.value.map(String) : [String(f.value)];
  return null;
}

/** The spec with an enum field picking these values (all of them, or none given: no condition). */
export function withPicked(
  spec: ViewSpec,
  field: string,
  values: string[],
  all: readonly string[],
): ViewSpec {
  const rest = parts(spec.filter).filter(
    (f) => !(isCond(f) && f.field === field && (f.op === 'in' || f.op === 'eq')),
  );
  const keep = values.length && values.length < all.length ? [{ field, op: 'in', value: values }] : [];
  return { ...spec, filter: join([...rest, ...keep]) };
}

/** The text a list searches one field for ("contains"), or ''. */
export function searched(spec: ViewSpec, field: string): string {
  const c = parts(spec.filter).find((f) => isCond(f) && f.field === field && f.op === 'contains');
  return c && isCond(c) ? String(c.value ?? '') : '';
}

/** The spec searching a text field for `text` (empty: no search). */
export function withSearch(spec: ViewSpec, field: string, text: string): ViewSpec {
  const rest = parts(spec.filter).filter((f) => !(isCond(f) && f.field === field && f.op === 'contains'));
  const t = text.trim();
  return { ...spec, filter: join([...rest, ...(t ? [{ field, op: 'contains', value: t }] : [])]) };
}

/** A header pressed: sort by it ascending, or flip it if it's already the sort. */
export function sortedBy(spec: ViewSpec, field: string): ViewSpec {
  const now = spec.sort?.[0];
  const dir = now?.field === field && now.dir === 'asc' ? 'desc' : 'asc';
  return { ...spec, sort: [{ field, dir }] };
}

/** A column shown or hidden; at least one stays. */
export function toggledColumn(spec: ViewSpec, field: string, shown: readonly string[]): ViewSpec {
  const cols = shown.includes(field) ? shown.filter((c) => c !== field) : [...shown, field];
  return cols.length ? { ...spec, columns: cols } : spec;
}

const words = (s: string) => s.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

/**
 * A cell as the office reads it: money, "Oct 15" (with its year when it isn't `now`'s: history fix 4),
 * "#24564", "Yes", a status in words, "—" when empty.
 */
export function cellText(
  entity: string,
  col: { field: string; type: string },
  v: unknown,
  timeZone: string,
  now: Date | string = new Date(),
): string {
  if (v === null || v === undefined || v === '') return '—';
  if (col.type === 'money') return money(Number(v));
  if (col.type === 'boolean') return v ? 'Yes' : 'No';
  if (col.type === 'date') return dateText(String(v).slice(0, 10), 'UTC');
  if (col.type === 'datetime') return dateTimeText(String(v), timeZone, { year: false, now });
  // a job's status says the app's one word for it (job-words); other enums their own value in words
  if (entity === 'jobs' && col.field === 'status') return jobStatusWord(String(v));
  if (col.type === 'enum') return words(String(v));
  if ((col.field === 'job' || (entity === 'jobs' && col.field === 'number')) && /^\w/.test(String(v)))
    return `#${String(v)}`;
  return String(v);
}

/** Two specs that would run the same list. */
export const sameSpec = (a: ViewSpec, b: ViewSpec): boolean =>
  JSON.stringify({ ...a, limit: undefined }) === JSON.stringify({ ...b, limit: undefined });
